import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib/utils';

export interface ThreadUnreadBadgeProps {
  /** Total messages in the thread. `0` → renders nothing. */
  messageCount: number;
  /** Unread messages for the current user. `> 0` → accent count badge. */
  unreadCount: number;
  className?: string;
  /**
   * Override the accessible label for the accent count badge. Defaults to the
   * message-thread wording ("N unread messages"). Non-thread reusers (e.g. a
   * pending-approvals count) should pass a domain-correct label.
   */
  ariaLabel?: string;
}

/**
 * ThreadUnreadBadge — the reusable read/unread indicator for an entity's
 * message thread. It is the visual companion to the generic
 * `useThreadLiveRefresh` / `useThreadMarkRead` hooks: any module with an
 * entity-attached thread (support ticket, task comments, …) can drop it next to
 * a thread affordance (a tab, a row) to surface unread activity at a glance.
 *
 * Three render states:
 * - **no messages** (`messageCount <= 0`) → renders `null` (nothing).
 * - **all read** (`messageCount > 0`, `unreadCount <= 0`) → a muted dot.
 * - **unread** (`unreadCount > 0`) → an accent count badge (caps at `99+`).
 *
 * Design-system tokens only (works in light + dark). For tab triggers prefer
 * the `messageCount` / `unreadCount` props on `TabsTriggerWithIndicator`, which
 * compose this badge inline.
 */
export function ThreadUnreadBadge({
  messageCount,
  unreadCount,
  className,
  ariaLabel,
}: ThreadUnreadBadgeProps) {
  const { t } = useTranslation('common');

  if (messageCount <= 0) {
    return null;
  }

  if (unreadCount > 0) {
    const display = unreadCount > 99 ? '99+' : String(unreadCount);
    return (
      <span
        role="status"
        aria-label={ariaLabel ?? t('thread.unreadMessages', { count: unreadCount })}
        className={cn(
          'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1',
          'text-[10px] font-semibold leading-none tabular-nums',
          'bg-primary text-primary-foreground',
          className
        )}
      >
        {display}
      </span>
    );
  }

  return (
    <span
      role="status"
      aria-label={t('thread.allRead')}
      className={cn('inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/50', className)}
    />
  );
}
